/**
 * Periodically spawns food items on walkable cells within the maze.
 *
 * Manages the lifecycle of active food items: creation, timing, and
 * removal (with fade-out animation handled by FoodItem itself).
 */

import Phaser from 'phaser';
import FoodItem from '../entities/FoodItem.js';
import MazeGenerator from './MazeGenerator.js';
import {
  FOOD_SPAWN_INTERVAL,
  MAX_ACTIVE_FOOD,
  FOOD_LIFETIME,
  FOOD_NEAR_PLAYER_CHANCE,
  TILE_SIZE,
  MIN_SPAWN_DISTANCE_FROM_PLAYER,
} from '../config/game-config.js';
import { FOOD_TYPES } from '../utils/constants.js';

export default class FoodSpawner {
  /**
   * @param {Phaser.Scene} scene       - The GameScene instance.
   * @param {number[][]}   grid        - The maze grid.
   * @param {Phaser.Physics.Arcade.Group} foodGroup - Physics group for food items.
   * @param {{ col: number, row: number }} playerPos - Reference to current player grid position.
   * @param {{ col: number, row: number }} exitPos   - Exit position to avoid spawning on.
   * @param {Set<string>} [forbiddenCells] - Set of "col,row" keys for hazard tiles (food must not spawn on water/lava).
   * @param {import('../entities/Player.js').default} [player] - Player entity (used to exclude movement destination cell).
   */
  constructor(scene, grid, foodGroup, playerPos, exitPos, forbiddenCells, player) {
    this.scene = scene;
    this.grid = grid;
    this.foodGroup = foodGroup;
    this.playerPos = playerPos;
    this.exitPos = exitPos;

    /** @type {Set<string>} Hazard tile positions (water + lava) — food cannot spawn here. */
    this.forbiddenCells = forbiddenCells || new Set();

    /** @type {import('../entities/Player.js').default|null} */
    this.player = player || null;

    /** @type {FoodItem[]} */
    this.activeItems = [];

    /**
     * Sibling spawner item lists — used to prevent food from spawning
     * on cells already occupied by coins, keys, chests, or the compass.
     * Set via {@link setSiblings} after all spawners are created.
     * @type {{ coins: Phaser.GameObjects.Sprite[]|null, keys: any[]|null, chests: any[]|null, compassRef: { item: import('../entities/CompassItem.js').default|null }|null }}
     */
    this._siblingCoins = null;
    this._siblingKeys = null;
    this._siblingChests = null;
    /** @type {{ item: import('../entities/CompassItem.js').default|null }|null} */
    this._compassRef = null;

    /** @type {Phaser.Time.TimerEvent|null} */
    this.spawnTimer = null;

    /**
     * Multiplier for food lifetime. Set > 0 to adjust how long food
     * persists before despawning. Default 1.0 (normal). Used by
     * LevelEventSystem for "Vorath's Hunger" (0.5).
     * @type {number}
     */
    this.lifetimeMultiplier = 1.0;
  }

  // ---------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------

  /**
   * Set references to all sibling spawner item lists so this spawner
   * avoids placing food on cells already occupied by other items.
   * @param {Phaser.GameObjects.Sprite[]|null} coins - CoinSpawner.activeItems
   * @param {any[]|null} keys - KeySpawner.activeItems
   * @param {any[]|null} chests - ChestSpawner.activeItems
   * @param {{ item: import('../entities/CompassItem.js').default|null }|null} compassRef - Object holding reference to the compass item (may be null if not spawned)
   */
  setSiblings(coins, keys, chests, compassRef, totemRef) {
    this._siblingCoins = coins;
    this._siblingKeys = keys;
    this._siblingChests = chests;
    this._compassRef = compassRef || null;
    this._totemRef = totemRef || null;
  }

  /** Start the spawn cycle. */
  start() {
    this.spawnTimer = this.scene.time.addEvent({
      delay: FOOD_SPAWN_INTERVAL,
      callback: this._trySpawn,
      callbackScope: this,
      loop: true,
    });

    // Spawn one immediately
    this._trySpawn();
  }

  /** Stop spawning and clean up all active items. */
  stop() {
    if (this.spawnTimer) {
      this.spawnTimer.destroy();
      this.spawnTimer = null;
    }
    this._destroyAllItems();
  }

  /** Called each frame to clean up expired items. */
  update() {
    this.activeItems = this.activeItems.filter((item) => {
      if (!item.active) {
        return false;
      }
      return true;
    });
  }

  // ---------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------

  /** @private Attempt to spawn a new food item if below the cap. */
  _trySpawn() {
    // Clean dead references first
    this.activeItems = this.activeItems.filter((item) => item.active);

    if (this.activeItems.length >= MAX_ACTIVE_FOOD) {
      return;
    }

    const cell = this._findSpawnCell();
    if (!cell) {
      return;
    }

    const foodType = Phaser.Utils.Array.GetRandom(FOOD_TYPES);
    const worldX = cell.col * TILE_SIZE + TILE_SIZE / 2;
    const worldY = cell.row * TILE_SIZE + TILE_SIZE / 2;

    const item = new FoodItem(this.scene, worldX, worldY, foodType, FOOD_LIFETIME * this.lifetimeMultiplier, this.grid);
    this.foodGroup.add(item);
    this.activeItems.push(item);
  }

  /**
   * Pick a walkable cell that is not occupied by the player,
   * exit, an existing food item, or any sibling item (coins,
   * keys, chests, compass).
   *
   * With probability FOOD_NEAR_PLAYER_CHANCE, the cell will be within
   * a moderate radius of the player so food is actually reachable.
   * Otherwise it picks a random walkable cell anywhere.
   *
   * All candidates must be at least MIN_SPAWN_DISTANCE_FROM_PLAYER
   * Manhattan distance from the player so items never appear on or
   * immediately adjacent to the player — even during a movement tween.
   *
   * @private
   * @returns {{ col: number, row: number }|null}
   */
  _findSpawnCell() {
    const pathCells = MazeGenerator.getPathCells(this.grid);

    // Build a set of occupied positions
    const occupied = new Set();
    occupied.add(`${this.playerPos.col},${this.playerPos.row}`);
    occupied.add(`${this.exitPos.col},${this.exitPos.row}`);

    // Also exclude the player's movement destination during an active tween
    // so food never appears on a cell the player is about to land on.
    if (this.player) {
      const dest = this.player.getDestination();
      if (dest) {
        occupied.add(`${dest.col},${dest.row}`);
      }
    }

    for (const item of this.activeItems) {
      if (item.active) {
        const c = Math.floor(item.x / TILE_SIZE);
        const r = Math.floor(item.y / TILE_SIZE);
        occupied.add(`${c},${r}`);
      }
    }

    // Also exclude cells occupied by sibling spawner items (coins, keys, chests, compass)
    const siblingLists = [this._siblingCoins, this._siblingKeys, this._siblingChests];
    for (const list of siblingLists) {
      if (list) {
        for (const item of list) {
          if (item && item.active) {
            // Use gridCol/gridRow if available (keys, chests), else compute from world coords
            const c = item.gridCol != null ? item.gridCol : Math.floor(item.x / TILE_SIZE);
            const r = item.gridRow != null ? item.gridRow : Math.floor(item.y / TILE_SIZE);
            occupied.add(`${c},${r}`);
          }
        }
      }
    }

    // Exclude compass item cell (if spawned and not yet collected)
    if (this._compassRef && this._compassRef.item && this._compassRef.item.active) {
      const ci = this._compassRef.item;
      const cc = ci.gridCol != null ? ci.gridCol : Math.floor(ci.x / TILE_SIZE);
      const cr = ci.gridRow != null ? ci.gridRow : Math.floor(ci.y / TILE_SIZE);
      occupied.add(`${cc},${cr}`);
    }

    // Exclude Threia Totem item cell (if spawned and not yet collected)
    if (this._totemRef && this._totemRef.item && this._totemRef.item.active) {
      const ti = this._totemRef.item;
      const tc = ti.gridCol != null ? ti.gridCol : Math.floor(ti.x / TILE_SIZE);
      const tr = ti.gridRow != null ? ti.gridRow : Math.floor(ti.y / TILE_SIZE);
      occupied.add(`${tc},${tr}`);
    }

    // Filter available cells: not occupied AND at least MIN_SPAWN_DISTANCE_FROM_PLAYER
    // Manhattan distance from the player. The distance check also prevents spawning
    // on the player's destination cell during a movement tween (dest is always 1 tile
    // from the reported playerPos).
    const pCol = this.playerPos.col;
    const pRow = this.playerPos.row;
    const minDist = MIN_SPAWN_DISTANCE_FROM_PLAYER;

    const available = pathCells.filter((cell) => {
      if (occupied.has(`${cell.col},${cell.row}`)) return false;
      // Never spawn food on hazard tiles (water/lava)
      if (this.forbiddenCells.has(`${cell.col},${cell.row}`)) return false;
      const dist = Math.abs(cell.col - pCol) + Math.abs(cell.row - pRow);
      return dist >= minDist;
    });

    if (available.length === 0) {
      return null;
    }

    // Try to spawn near the player most of the time
    if (Math.random() < FOOD_NEAR_PLAYER_CHANCE) {
      const near = this._findNearPlayerCell(available);
      if (near) return near;
    }

    // Fallback: random cell anywhere (already filtered by min distance)
    return Phaser.Utils.Array.GetRandom(available);
  }

  /**
   * Find a walkable cell within a moderate radius ahead of or around
   * the player. Picks from cells 3-10 tiles away (close enough to
   * reach, far enough to not be trivial).
   *
   * @private
   * @param {{ col: number, row: number }[]} available
   * @returns {{ col: number, row: number }|null}
   */
  _findNearPlayerCell(available) {
    const pCol = this.playerPos.col;
    const pRow = this.playerPos.row;
    const minDist = 3;
    const maxDist = 12;

    const nearby = available.filter((cell) => {
      const dx = cell.col - pCol;
      const dy = cell.row - pRow;
      const dist = Math.abs(dx) + Math.abs(dy); // Manhattan distance
      return dist >= minDist && dist <= maxDist;
    });

    if (nearby.length === 0) {
      return null;
    }

    return Phaser.Utils.Array.GetRandom(nearby);
  }

  /** @private */
  _destroyAllItems() {
    for (const item of this.activeItems) {
      if (item.active) {
        item.destroy();
      }
    }
    this.activeItems = [];
  }
}
